/**
 * TextAnnotation — renders an editable text label.
 *
 * Supports configurable fontSize, fontFamily, bold, italic, color.
 * Uses the same rendering approach as the existing TextOverlay but
 * operates in pure image-pixel coordinates with no IViewport dependency.
 */

import type {
    AnnotationInteractionEvent,
    AnnotationStyle,
    IAnnotationObject,
    TextAnnotationData,
} from '../types';
import { AnnotationType, DEFAULT_ANNOTATION_STYLE } from '../types';

const HIT_PADDING = 4;

interface TextDragState {
    startPosition: { x: number; y: number };
}

export class TextAnnotation implements IAnnotationObject {
    readonly id: string;
    readonly interactive = true;
    visible = true;
    selected = false;
    readonly zIndex: number;

    position: { x: number; y: number };
    text: string;
    style: AnnotationStyle;

    /**
     * True while the text is being edited in place by the DOM overlay editor.
     * Transient (never serialised): suppresses canvas rendering and hit
     * testing so the overlay is the single visual source of truth.
     */
    isEditing = false;

    hoveredHandle: 'body' | null = null;

    private textMetrics: TextMetrics | null = null;
    private dragState: TextDragState | null = null;

    constructor(options: {
        id: string;
        position: { x: number; y: number };
        text: string;
        style?: Partial<AnnotationStyle>;
        zIndex?: number;
    }) {
        this.id = options.id;
        this.position = { ...options.position };
        this.text = options.text;
        this.style = { ...DEFAULT_ANNOTATION_STYLE, ...options.style };
        this.zIndex = options.zIndex ?? 110;
    }

    // ── Hit testing ──

    hitTest(x: number, y: number): boolean {
        return this.getHandleAtPoint(x, y) !== null;
    }

    getHandleAtPoint(x: number, y: number): 'body' | null {
        if (this.isEditing || !this.textMetrics) return null;

        const { width } = this.textMetrics;
        const height = this.style.fontSize;
        const px = this.position.x;
        const py = this.position.y;

        if (
            x >= px - HIT_PADDING &&
            x <= px + width + HIT_PADDING &&
            y >= py - height - HIT_PADDING &&
            y <= py + HIT_PADDING
        ) {
            return 'body';
        }
        return null;
    }

    // ── Interaction ──

    clearHoverState(): void {
        this.hoveredHandle = null;
    }

    handleEvent(event: AnnotationInteractionEvent): boolean {
        switch (event.type) {
            case 'hover': {
                const handle = this.getHandleAtPoint(event.currentPoint.x, event.currentPoint.y);
                if (handle) {
                    this.hoveredHandle = handle;
                    return true;
                }
                return false;
            }
            case 'pointer_down': {
                if (!this.getHandleAtPoint(event.currentPoint.x, event.currentPoint.y)) {
                    return false;
                }
                this.dragState = { startPosition: { ...this.position } };
                return true;
            }
            case 'pointer_move': {
                if (!this.dragState) return false;
                const dx = event.currentPoint.x - event.startPoint.x;
                const dy = event.currentPoint.y - event.startPoint.y;
                this.position = {
                    x: this.dragState.startPosition.x + dx,
                    y: this.dragState.startPosition.y + dy,
                };
                return true;
            }
            case 'pointer_up': {
                this.dragState = null;
                return true;
            }
        }
    }

    // ── Placement ──

    updatePlacement(): void {
        /* Text is placed immediately — no drag-to-create phase. */
    }

    isValidPlacement(): boolean {
        return true;
    }

    // ── Mutation helpers ──

    setText(text: string): void {
        this.text = text;
    }

    updateStyle(partialStyle: Partial<AnnotationStyle>): void {
        Object.assign(this.style, partialStyle);
    }

    // ── Serialisation ──

    serialize(): TextAnnotationData {
        return {
            type: AnnotationType.TEXT,
            id: this.id,
            position: { ...this.position },
            text: this.text,
            style: { ...this.style },
        };
    }

    static hydrate(data: TextAnnotationData): TextAnnotation {
        return new TextAnnotation({
            id: data.id,
            position: data.position,
            text: data.text,
            style: data.style,
        });
    }

    // ── Rendering ──

    render(ctx: CanvasRenderingContext2D): void {
        if (!this.visible || this.isEditing || !this.text) return;

        const { position, text, style } = this;

        ctx.save();

        const weight = style.bold ? 'bold' : 'normal';
        const fontStyle = style.italic ? 'italic' : 'normal';
        ctx.font = `${fontStyle} ${weight} ${style.fontSize}px ${style.fontFamily}`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'bottom';

        this.textMetrics = ctx.measureText(text);

        // Background box for readability
        const padding = 3;
        const w = this.textMetrics.width + padding * 2;
        const h = style.fontSize + padding * 2;
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(position.x - padding, position.y - style.fontSize - padding, w, h);

        // Text
        ctx.fillStyle = style.color;
        ctx.fillText(text, position.x, position.y);

        // Selection / hover indicator — selection is drawn persistently.
        if (this.selected || this.hoveredHandle === 'body') {
            ctx.strokeStyle = this.selected ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.6)';
            ctx.lineWidth = 1;
            ctx.setLineDash([3, 3]);
            ctx.strokeRect(position.x - padding, position.y - style.fontSize - padding, w, h);
        }

        ctx.restore();
    }
}
